// ─── Universal Storage Layer ──────────────────────────────────
// "local"    → localStorage (device only, no login needed)
// "firebase" → Firestore cloud (requires Google login)
// "gdrive"   → Google Drive (user's own Drive)
// ─────────────────────────────────────────────────────────────

import {
  collection, doc, getDocs, getDoc, setDoc,
  deleteDoc, onSnapshot, serverTimestamp, writeBatch
} from "firebase/firestore";
import { db } from "./firebase";
import { sanitizeAccountForStorage, sanitizeTransactionForStorage, sanitizeAmount, sanitizeFeedbackForStorage } from "./utils/security";
import { sanitizeActivityLog, pruneActivityLogs, getActivityTimestamp } from "./utils/activityLog";
import { getLocalVaultDataSync, getLocalVaultData, persistLocalVaultData, getLocalVaultApproxBytes, getLocalVaultSecurityStatus, migrateLocalVaultToIndexedDB } from "./utils/localVaultSecurity";

const STORAGE_KEY = "finvault_storage_mode";

// ── Mode helpers ──────────────────────────────────────────────
export const getStorageMode = () => localStorage.getItem(STORAGE_KEY) || null;
export const setStorageMode = (mode) => localStorage.setItem(STORAGE_KEY, mode);

// ── ID generator ──────────────────────────────────────────────
export const uid2 = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

// ═══════════════════════════════════════════════════════════════
// LOCAL STORAGE HELPERS
// ═══════════════════════════════════════════════════════════════
export function getLocalSnapshot() {
  return getLocalVaultDataSync();
}

export async function upgradeLocalStorageBackend() {
  return migrateLocalVaultToIndexedDB();
}

export function getLocalVaultSizeBytes() {
  return getLocalVaultApproxBytes();
}

export function getLocalVaultStatus() {
  return getLocalVaultSecurityStatus();
}

async function localGet() {
  return await getLocalVaultData();
}
async function localSet(data) {
  await persistLocalVaultData(data);
}
async function localGetAccounts() {
  const d = await localGet();
  return Object.values(d.accounts||{});
}
async function localGetTxns(accountId) {
  const d = await localGet();
  return Object.values((d.transactions||{})[accountId]||{});
}
async function localSaveAccount(acc) {
  const d=await localGet();
  if(!d.accounts) d.accounts={};
  d.accounts[acc.id]=acc;
  await localSet(d);
}
async function localSaveTxn(accountId,tx) {
  const d=await localGet();
  if(!d.transactions) d.transactions={};
  if(!d.transactions[accountId]) d.transactions[accountId]={};
  d.transactions[accountId][tx.id]=tx;
  await localSet(d);
}
async function localDeleteAccount(accountId) {
  const d=await localGet();
  delete (d.accounts||{})[accountId];
  delete (d.transactions||{})[accountId];
  delete (d.activityLogs||{})[accountId];
  await localSet(d);
}
async function localDeleteTxn(accountId,txId) {
  const d=await localGet();
  delete ((d.transactions||{})[accountId]||{})[txId];
  await localSet(d);
}
async function localSaveActivityLog(accountId, log) {
  const d = await localGet();
  if (!d.activityLogs) d.activityLogs = {};
  if (!d.activityLogs[accountId]) d.activityLogs[accountId] = {};
  const safe = sanitizeActivityLog(log);
  if (!safe.id) safe.id = uid2();
  d.activityLogs[accountId][safe.id] = safe;
  const pruned = pruneActivityLogs(Object.values(d.activityLogs[accountId]));
  d.activityLogs[accountId] = Object.fromEntries(pruned.map(item => [item.id || uid2(), item]));
  await localSet(d);
  return safe.id;
}
async function localSaveGlobalActivityLog(log) {
  const d = await localGet();
  if (!d.globalActivityLogs) d.globalActivityLogs = {};
  const safe = sanitizeActivityLog(log);
  if (!safe.id) safe.id = uid2();
  d.globalActivityLogs[safe.id] = safe;
  const pruned = pruneActivityLogs(Object.values(d.globalActivityLogs));
  d.globalActivityLogs = Object.fromEntries(pruned.map(item => [item.id || uid2(), item]));
  await localSet(d);
  return safe.id;
}
async function localUpdateAccount(accountId,updates) {
  const d=await localGet();
  if(d.accounts&&d.accounts[accountId]) {
    d.accounts[accountId]={...d.accounts[accountId],...updates};
    await localSet(d);
  }
}

// ═══════════════════════════════════════════════════════════════
// GOOGLE DRIVE HELPERS
// ═══════════════════════════════════════════════════════════════
const GDRIVE_FOLDER="FinVault";
let gdriveToken=null;
export function setGDriveToken(token){gdriveToken=token;}
export function getGDriveToken(){return gdriveToken;}

async function gdriveReq(url,opts={}){
  const res=await fetch(url,{...opts,headers:{"Authorization":`Bearer ${gdriveToken}`,"Content-Type":"application/json",...(opts.headers||{})}});
  if(!res.ok) throw new Error(`Drive ${res.status}`);
  if(res.status===204) return {};
  return res.json();
}
async function getOrCreateFolder(){
  const s=await gdriveReq(`https://www.googleapis.com/drive/v3/files?q=name='${GDRIVE_FOLDER}' and mimeType='application/vnd.google-apps.folder' and trashed=false&fields=files(id)`);
  if(s.files&&s.files.length>0) return s.files[0].id;
  const f=await gdriveReq("https://www.googleapis.com/drive/v3/files",{method:"POST",body:JSON.stringify({name:GDRIVE_FOLDER,mimeType:"application/vnd.google-apps.folder"})});
  return f.id;
}
async function gdriveListFiles(folderId){
  const r=await gdriveReq(`https://www.googleapis.com/drive/v3/files?q='${folderId}' in parents and trashed=false&fields=files(id,name)`);
  return r.files||[];
}
async function gdriveReadFile(fileId){
  const res=await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,{headers:{"Authorization":`Bearer ${gdriveToken}`}});
  return res.json();
}
async function gdriveSaveFile(folderId,fileName,data,existingId=null){
  const content=JSON.stringify(data);
  const boundary="fv_boundary_xyz";
  const meta=existingId?{}:{name:fileName,parents:[folderId]};
  if(existingId){
    await fetch(`https://www.googleapis.com/upload/drive/v3/files/${existingId}?uploadType=media`,{
      method:"PATCH",headers:{"Authorization":`Bearer ${gdriveToken}`,"Content-Type":"application/json"},body:content});
  } else {
    const body=`--${boundary}\r\nContent-Type: application/json\r\n\r\n${JSON.stringify(meta)}\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n${content}\r\n--${boundary}--`;
    await fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",{
      method:"POST",headers:{"Authorization":`Bearer ${gdriveToken}`,"Content-Type":`multipart/related; boundary=${boundary}`},body});
  }
}

// ═══════════════════════════════════════════════════════════════
// UNIFIED API
// ═══════════════════════════════════════════════════════════════

// ── ACCOUNTS ─────────────────────────────────────────────────

export async function loadAccounts(uid, mode, onUpdate) {
  // LOCAL — IndexedDB/local-vault callback
  if (mode==="local") {
    onUpdate(await localGetAccounts());
    return () => {};
  }
  // FIREBASE — real-time listener
  if (mode==="firebase") {
    const col=collection(db,"users",uid,"accounts");
    return onSnapshot(col, snap=>{
      onUpdate(snap.docs.map(d=>({id:d.id,...d.data()})));
    });
  }
  // GDRIVE — one-time load
  if (mode==="gdrive") {
    try {
      const folderId=await getOrCreateFolder();
      const files=await gdriveListFiles(folderId);
      const accs=await Promise.all(files.map(f=>gdriveReadFile(f.id)));
      onUpdate(accs.filter(a=>a&&a.id&&a.profileType));
    } catch(e) { onUpdate([]); }
    return ()=>{};
  }
  onUpdate([]);
  return ()=>{};
}

export async function saveAccount(uid, mode, account) {
  const acc = sanitizeAccountForStorage(account);

  if(mode==="local"){
    if(!acc.id) acc.id=uid2();
    await localSaveAccount(acc);
    return acc.id;
  }
  if(mode==="firebase"){
    const ref2=acc.id
      ? doc(db,"users",uid,"accounts",acc.id)
      : doc(collection(db,"users",uid,"accounts"));
    await setDoc(ref2,{...acc,id:ref2.id,createdAt:serverTimestamp()},{merge:true});
    return ref2.id;
  }
  if(mode==="gdrive"){
    if(!acc.id) acc.id=uid2();
    const folderId=await getOrCreateFolder();
    const files=await gdriveListFiles(folderId);
    const existing=files.find(f=>f.name===`${acc.id}.json`);
    // Store account without txns (keep txns separate inside same file)
    const existing_data=existing?await gdriveReadFile(existing.id):{};
    await gdriveSaveFile(folderId,`${acc.id}.json`,{...existing_data,...acc},existing?.id);
    return acc.id;
  }
  return uid2();
}

// Mark exactly one profile as the user's startup/default profile.
// Manual profile switching never changes this setting.
export async function setPrimaryAccount(uid, mode, accountId) {
  if (!accountId) throw new Error("Select a profile to make primary.");

  if (mode === "local") {
    const data = await localGet();
    const accounts = data.accounts || {};
    if (!accounts[accountId]) throw new Error("Primary profile was not found.");
    Object.keys(accounts).forEach(id => {
      accounts[id] = { ...accounts[id], isPrimary: id === accountId };
    });
    data.accounts = accounts;
    await localSet(data);
    return accountId;
  }

  if (mode === "firebase") {
    const col = collection(db, "users", uid, "accounts");
    const snap = await getDocs(col);
    if (!snap.docs.some(item => item.id === accountId)) throw new Error("Primary profile was not found.");
    const batch = writeBatch(db);
    snap.docs.forEach(item => {
      const shouldBePrimary = item.id === accountId;
      if (shouldBePrimary || item.data()?.isPrimary === true) {
        batch.set(item.ref, { isPrimary: shouldBePrimary }, { merge: true });
      }
    });
    await batch.commit();
    return accountId;
  }

  if (mode === "gdrive") {
    const folderId = await getOrCreateFolder();
    const files = await gdriveListFiles(folderId);
    const profileFiles = files.filter(file => file.name.endsWith(".json") && !file.name.startsWith("_"));
    let found = false;
    const records = [];
    for (const file of profileFiles) {
      const data = await gdriveReadFile(file.id);
      if (!data?.id || !data?.profileType) continue;
      if (data.id === accountId) found = true;
      records.push({ file, data });
    }
    if (!found) throw new Error("Primary profile was not found.");
    for (const { file, data } of records) {
      const shouldBePrimary = data.id === accountId;
      if (shouldBePrimary || data.isPrimary === true) {
        await gdriveSaveFile(folderId, file.name, { ...data, isPrimary: shouldBePrimary }, file.id);
      }
    }
    return accountId;
  }

  throw new Error("Unsupported storage mode.");
}

export async function removeAccount(uid, mode, accountId) {
  if(mode==="local"){ await localDeleteAccount(accountId); return; }
  if(mode==="firebase"){
    const txCol=collection(db,"users",uid,"accounts",accountId,"transactions");
    const logCol=collection(db,"users",uid,"accounts",accountId,"activityLogs");
    const txSnap=await getDocs(txCol);
    const logSnap=await getDocs(logCol);
    const batch=writeBatch(db);
    txSnap.docs.forEach(d=>batch.delete(d.ref));
    logSnap.docs.forEach(d=>batch.delete(d.ref));
    batch.delete(doc(db,"users",uid,"accounts",accountId));
    await batch.commit();
    return;
  }
  if(mode==="gdrive"){
    const folderId=await getOrCreateFolder();
    const files=await gdriveListFiles(folderId);
    const file=files.find(f=>f.name===`${accountId}.json`);
    if(file) await gdriveReq(`https://www.googleapis.com/drive/v3/files/${file.id}`,{method:"DELETE"});
  }
}

// ── TRANSACTIONS ──────────────────────────────────────────────

export async function loadTransactions(uid, mode, accountId, onUpdate) {
  if(mode==="local"){
    onUpdate(await localGetTxns(accountId));
    return ()=>{};
  }
  if(mode==="firebase"){
    const col=collection(db,"users",uid,"accounts",accountId,"transactions");
    return onSnapshot(col, snap=>{
      onUpdate(snap.docs.map(d=>({id:d.id,...d.data()})));
    });
  }
  if(mode==="gdrive"){
    try {
      const folderId=await getOrCreateFolder();
      const files=await gdriveListFiles(folderId);
      const file=files.find(f=>f.name===`${accountId}.json`);
      if(file){ const acc=await gdriveReadFile(file.id); onUpdate(Object.values(acc.txns||{})); }
      else onUpdate([]);
    } catch { onUpdate([]); }
    return ()=>{};
  }
  onUpdate([]);
  return ()=>{};
}

export async function saveTxn(uid, mode, accountId, tx, profileType = tx?.profileType || "standard") {
  tx = sanitizeTransactionForStorage(tx, profileType);
  if(!tx.id) tx={...tx,id:uid2()};
  if(mode==="local"){ await localSaveTxn(accountId,tx); return tx.id; }
  if(mode==="firebase"){
    const ref2=doc(db,"users",uid,"accounts",accountId,"transactions",tx.id);
    await setDoc(ref2,{...tx,createdAt:serverTimestamp()},{merge:true});
    return tx.id;
  }
  if(mode==="gdrive"){
    const folderId=await getOrCreateFolder();
    const files=await gdriveListFiles(folderId);
    const file=files.find(f=>f.name===`${accountId}.json`);
    if(file){
      const acc=await gdriveReadFile(file.id);
      if(!acc.txns) acc.txns={};
      acc.txns[tx.id]=tx;
      await gdriveSaveFile(folderId,`${accountId}.json`,acc,file.id);
    }
    return tx.id;
  }
  return tx.id;
}

export async function removeTxn(uid, mode, accountId, txId) {
  if(mode==="local"){ await localDeleteTxn(accountId,txId); return; }
  if(mode==="firebase"){
    await deleteDoc(doc(db,"users",uid,"accounts",accountId,"transactions",txId));
    return;
  }
  if(mode==="gdrive"){
    const folderId=await getOrCreateFolder();
    const files=await gdriveListFiles(folderId);
    const file=files.find(f=>f.name===`${accountId}.json`);
    if(file){
      const acc=await gdriveReadFile(file.id);
      delete (acc.txns||{})[txId];
      await gdriveSaveFile(folderId,`${accountId}.json`,acc,file.id);
    }
  }
}

// ── ACTIVITY LOGS ─────────────────────────────────────────────

export async function saveActivityLog(uid, mode, accountId, log) {
  const safe = sanitizeActivityLog(log);
  if (!safe.id) safe.id = uid2();
  const targetAccountId = accountId || safe.accountId || "global";
  safe.accountId = targetAccountId;

  if (mode === "local") return await localSaveActivityLog(targetAccountId, safe);

  if (mode === "firebase") {
    const ref2 = doc(db, "users", uid, "accounts", targetAccountId, "activityLogs", safe.id);
    await setDoc(ref2, safe, { merge: true });

    // Lightweight 30-day cleanup. Do not block normal app flow if cleanup fails.
    try {
      const snap = await getDocs(collection(db, "users", uid, "accounts", targetAccountId, "activityLogs"));
      const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
      const batch = writeBatch(db);
      let count = 0;
      snap.docs.forEach(item => {
        if (getActivityTimestamp(item.data()) < cutoff) { batch.delete(item.ref); count += 1; }
      });
      if (count) await batch.commit();
    } catch (_) {}
    return safe.id;
  }

  if (mode === "gdrive") {
    const folderId = await getOrCreateFolder();
    const files = await gdriveListFiles(folderId);
    const file = files.find(f => f.name === `${targetAccountId}.json`);
    if (file) {
      const acc = await gdriveReadFile(file.id);
      if (!acc.activityLogs) acc.activityLogs = {};
      acc.activityLogs[safe.id] = safe;
      const pruned = pruneActivityLogs(Object.values(acc.activityLogs));
      acc.activityLogs = Object.fromEntries(pruned.map(item => [item.id || uid2(), item]));
      await gdriveSaveFile(folderId, `${targetAccountId}.json`, acc, file.id);
    }
    return safe.id;
  }
  return safe.id;
}

export async function saveGlobalActivityLog(uid, mode, log) {
  const safe = sanitizeActivityLog({ ...log, accountId: "global" });
  if (!safe.id) safe.id = uid2();

  if (mode === "local") return await localSaveGlobalActivityLog(safe);

  if (mode === "firebase") {
    const ref2 = doc(db, "users", uid, "activityLogs", safe.id);
    await setDoc(ref2, safe, { merge: true });

    // Keep global/export logs under the same 30-day retention policy.
    try {
      const snap = await getDocs(collection(db, "users", uid, "activityLogs"));
      const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
      const batch = writeBatch(db);
      let count = 0;
      snap.docs.forEach(item => {
        if (getActivityTimestamp(item.data()) < cutoff) { batch.delete(item.ref); count += 1; }
      });
      if (count) await batch.commit();
    } catch (_) {}
    return safe.id;
  }

  if (mode === "gdrive") {
    const folderId = await getOrCreateFolder();
    const files = await gdriveListFiles(folderId);
    const fileName = "_global_activity_logs.json";
    const file = files.find(f => f.name === fileName);
    const data = file ? await gdriveReadFile(file.id) : { activityLogs: {} };
    if (!data.activityLogs) data.activityLogs = {};
    data.activityLogs[safe.id] = safe;
    const pruned = pruneActivityLogs(Object.values(data.activityLogs));
    data.activityLogs = Object.fromEntries(pruned.map(item => [item.id || uid2(), item]));
    await gdriveSaveFile(folderId, fileName, data, file?.id);
    return safe.id;
  }
  return safe.id;
}


// ── FEEDBACK ──────────────────────────────────────────────────

export async function saveFeedback(uid, mode, feedback = {}) {
  if (mode !== "firebase") {
    throw new Error("Feedback submission is available in Cloud mode so it can be reviewed securely.");
  }

  const day = new Date().toISOString().split("T")[0];
  const base = sanitizeFeedbackForStorage({
    ...feedback,
    userId: uid,
    feedbackDate: day,
    createdAt: new Date().toISOString(),
    createdAtMs: Date.now()
  });

  if (!base.message) throw new Error("Please enter feedback before submitting.");
  if (!uid || uid === "local_user") throw new Error("Please sign in with Cloud mode to submit feedback.");

  const dayRef = doc(db, "users", uid, "feedback", day);
  const snap = await getDoc(dayRef);
  const current = snap.exists() ? snap.data() : {};
  const usedSlots = [1, 2, 3].filter(n => current[`entry${n}`]);
  if (usedSlots.length >= 3) {
    throw new Error("Daily feedback limit reached. You can submit up to 3 feedback messages per day.");
  }

  const slot = [1, 2, 3].find(n => !current[`entry${n}`]) || 1;
  const id = `${day}_${slot}`;
  const entry = sanitizeFeedbackForStorage({ ...base, id, slot });
  const update = {
    userId: uid,
    feedbackDate: day,
    count: Math.max(Number(current.count || 0), slot),
    updatedAt: serverTimestamp(),
    [`entry${slot}`]: entry
  };
  await setDoc(dayRef, update, { merge: true });
  return id;
}

export async function loadActivityLogsForExport(uid, mode, accounts = []) {
  const result = {};
  const ids = (accounts || []).map(a => a.id).filter(Boolean);
  ids.forEach(id => { result[id] = []; });
  result.global = [];

  if (mode === "local") {
    const d = await localGet();
    ids.forEach(id => { result[id] = pruneActivityLogs(Object.values((d.activityLogs||{})[id]||{})); });
    result.global = pruneActivityLogs(Object.values(d.globalActivityLogs || {}));
    return result;
  }

  if (mode === "firebase") {
    for (const id of ids) {
      const snap = await getDocs(collection(db, "users", uid, "accounts", id, "activityLogs"));
      result[id] = pruneActivityLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    try {
      const snap = await getDocs(collection(db, "users", uid, "activityLogs"));
      result.global = pruneActivityLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (_) {}
    return result;
  }

  if (mode === "gdrive") {
    const folderId = await getOrCreateFolder();
    const files = await gdriveListFiles(folderId);
    for (const id of ids) {
      const file = files.find(f => f.name === `${id}.json`);
      if (file) {
        const acc = await gdriveReadFile(file.id);
        result[id] = pruneActivityLogs(Object.values(acc.activityLogs || {}));
      }
    }
    const globalFile = files.find(f => f.name === "_global_activity_logs.json");
    if (globalFile) {
      const data = await gdriveReadFile(globalFile.id);
      result.global = pruneActivityLogs(Object.values(data.activityLogs || {}));
    }
    return result;
  }

  return result;
}

// ── CURRENT VALUE ─────────────────────────────────────────────

export async function saveCurrentValue(uid, mode, accountId, value, history) {
  const safeValue = sanitizeAmount(value);
  const safeHistory = Array.isArray(history)
    ? history.slice(0, 90).map(h => ({
        id: String(h.id || uid2()).slice(0, 120),
        date: /^\d{4}-\d{2}-\d{2}$/.test(String(h.date || "")) ? h.date : new Date().toISOString().split("T")[0],
        value: sanitizeAmount(h.value)
      }))
    : [];
  if(mode==="local"){
    await localUpdateAccount(accountId,{currentValue:safeValue,cvHistory:safeHistory});
    return;
  }
  if(mode==="firebase"){
    const {updateDoc}=await import("firebase/firestore");
    await updateDoc(doc(db,"users",uid,"accounts",accountId),{currentValue:safeValue,cvHistory:safeHistory});
    return;
  }
  if(mode==="gdrive"){
    const folderId=await getOrCreateFolder();
    const files=await gdriveListFiles(folderId);
    const file=files.find(f=>f.name===`${accountId}.json`);
    if(file){
      const acc=await gdriveReadFile(file.id);
      acc.currentValue=safeValue; acc.cvHistory=safeHistory;
      await gdriveSaveFile(folderId,`${accountId}.json`,acc,file.id);
    }
  }
}
